import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button } from '../../../../components/ui/Button.js';
import { DataTable } from '@repo/web-list';
import { defineListPage } from '@repo/list';
import { Plus, Scale } from 'lucide-react';
import { useFeeSlabs, useDeleteFeeSlab, type FeeSlab } from '../../../../features/settings/network/fee-slabs.api';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { usePageTitle } from '../../../../lib/hooks';
import { useToast } from '../../../../components/ui/Toast';

export const Route = createFileRoute('/_auth/settings/fee-slabs/')({
  component: FeeSlabsSettingsPage,
});

const feeSlabsConfig = defineListPage<FeeSlab>({
  variant: 'data-table',
  resource: 'fee-slabs',
  searchable: true,
  columns: [
    { id: 'feeType', header: 'Fee Type', accessor: 'feeType', sortable: true },
    { id: 'calculationType', header: 'Calculation Type', accessor: 'calculationType' },
    { id: 'effectiveFrom', header: 'Effective From', accessor: 'effectiveFrom' },
    { id: 'config', header: 'Configuration', accessor: () => null },
  ],
});

function FeeSlabsSettingsPage() {
  usePageTitle('Fee Slabs Settings');
  const navigate = useNavigate();
  const { toast } = useToast();
  const deleteFeeSlab = useDeleteFeeSlab();
  const [query, setQuery] = React.useState('');

  const { data: feeSlabsList = [], refetch, isLoading } = useFeeSlabs({ feeType: query || undefined });

  const handleDeleteFeeSlab = async (id: string) => {
    try {
      await deleteFeeSlab.mutateAsync(id);
      toast({
        type: 'success',
        title: 'Submitted for Approval',
        description: 'Fee slab deletion request has been submitted for approval.',
      });
      void refetch();
    } catch (err) {
      console.error('Failed to delete fee slab:', err);
      toast({
        type: 'error',
        title: 'Error',
        description: 'Failed to delete fee slab. Please try again.',
      });
    }
  };

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Fee Slabs"
        subtitle="Configure penalty, late fee, and service fee slabs for automatic ledger postings."
        action={
          <Button variant="primary" onClick={() => navigate({ to: '/settings/fee-slabs/new' as const })}>
            <Plus className="w-4 h-4 mr-2" />
            Add Fee Slab
          </Button>
        }
      />

      <div className="flex-1 w-full min-h-0">
        <DataTable
          config={feeSlabsConfig}
          data={feeSlabsList}
          total={feeSlabsList.length}
          page={1}
          limit={feeSlabsList.length || 1}
          onPageChange={() => {}}
          loading={isLoading}
          onRetry={refetch}
          search={query}
          onSearchChange={setQuery}
          rowActions={[
            {
              label: 'Delete Fee Slab',
              variant: 'destructive',
              requiresConfirmation: true,
              onClick: (row) => void handleDeleteFeeSlab(row.id),
            },
          ]}
          customRenderers={{
            feeType: (row) => (
              <div className="flex items-center gap-2">
                <Scale className="w-4 h-4 text-accent-500 shrink-0" />
                <span className="font-semibold">{row.feeType}</span>
              </div>
            ),
            calculationType: (row) => (
              <span className="font-mono">{row.calculationType}</span>
            ),
            effectiveFrom: (row) => (
              <span>{row.effectiveFrom}</span>
            ),
            config: (row) => (
              <pre className="text-xs text-text-secondary bg-surface-3 p-2 rounded max-w-xs overflow-auto">
                {JSON.stringify(row.config, null, 2)}
              </pre>
            ),
          }}
        />
      </div>
    </div>
  );
}
